import React, { useRef, useEffect } from "react";
import { RouteComponentProps, StaticContext } from "react-router";
import { Box, Typography } from "@material-ui/core";
import {
  ActionLinks,
  Icon,
  IconName,
  IActionLink,
  MyTripsMobileCard,
  BannerSeverity,
  NotificationBanner,
} from "halifax";
import clsx from "clsx";
import {
  HomesItinerary,
  HomesReservationStatus,
  ItineraryEnum,
  ListingCollectionEnum,
} from "redmond";

import * as textConstants from "./constants";
import { scrollTopWithOffset } from "../../component";
import "./styles.scss";
import { DesktopHomeCard } from "./components/DesktopHomeCard";
import { HomeCardConnectorProps } from "./container";
import { buildExploreActivitiesActions } from "../../helpers";
import { useIsTravelApp } from "@capone/common";

export const getStatusBanner = (status?: HomesReservationStatus) => {
  switch (status) {
    case HomesReservationStatus.Cancelled:
      return (
        <NotificationBanner
          className="status-banner"
          label={textConstants.CANCELED_RES}
          severity={BannerSeverity.ERROR}
          icon={<Icon name={IconName.ErrorAlert} />}
        />
      );
    default:
      return undefined;
  }
};

interface IHomeCardProps
  extends HomeCardConnectorProps,
  RouteComponentProps<{}, StaticContext, { prevPath?: string }> {
  isMobile?: boolean;
  home: HomesItinerary;
  expandedCard: string;
  onExpandCard: (cardId: string) => void;
  showActivitiesXSell?: boolean;
}

export const HomeCard = ({
  history,
  home,
  isMobile,
  setSelectedHome,
  populateTripQueryParams,
  expandedCard,
  isPastTrips,
  onExpandCard,
  setOpenModal,
  showActivitiesXSell = false,
}: IHomeCardProps) => {
  const homeRef = useRef<HTMLDivElement | null>(null);
  const { isTravelApp } = useIsTravelApp();

  useEffect(() => {
    if (
      !isMobile &&
      homeRef?.current &&
      expandedCard === home.reservation.id.value
    ) {
      scrollTopWithOffset(homeRef.current);
    }
  }, [expandedCard]);

  const reservationState = home.reservation.status.ReservationStatus;
  const statusBanner = getStatusBanner(reservationState);
  const isCancel = reservationState == HomesReservationStatus.Cancelled;
  const confirmationCodeClassName = isCancel ? "warning" : "";

  const moreActions: IActionLink[] = showActivitiesXSell && home.reservation.listing.areaName
    ? buildExploreActivitiesActions({
      cityLabel: home.reservation.listing.areaName,
      fallbackLabel: `${home.reservation.listing.areaName}${home.reservation.listing.state ? `, ${home.reservation.listing.state}` : ""}${home.reservation.listing.countryCode ? `, ${home.reservation.listing.countryCode}` : ""}`,
      coords: home.reservation.listing.location?.coordinates,
      fromDate: home.reservation.stayDates.from,
      untilDate: home.reservation.stayDates.until,
      isTravelApp,
      history,
      product: ItineraryEnum.Home,
    })
    : [];
  return (
    <Box className={clsx({ mobile: isMobile }, "home-list")}>
      {isMobile ? (
        <MyTripsMobileCard
          onClick={() => {
            setSelectedHome(home);
            populateTripQueryParams(history);
          }}
          banner={statusBanner}
          confirmationCodeClassName={confirmationCodeClassName}
          confirmationLabel={textConstants.CONFIRMATION}
          label={
            home.reservation.listingCollection ===
              ListingCollectionEnum.Premier ||
              home.reservation.listingCollection ===
              ListingCollectionEnum.Lifestyle
              ? textConstants.MOBILE_PREMIUM_VR_LABEL
              : textConstants.MOBILE_VR_LABEL
          }
          icon={IconName.HomeIcon}
          bottomContent={
            moreActions.length > 0 ? (
              <ActionLinks className="trip-info-bottom-content" actions={moreActions} />
            ) : undefined
          }
          {...textConstants.getHomeInfoDetails(home)}
        />
      ) : (
        <div ref={homeRef}>
          <Box className="home-card-icon-title">
            <Icon name={IconName.HomeIcon} />
            <Typography variant="body1" className="summary-label">
              {textConstants.getHeaderText(
                home.reservation.stayDates.from,
                home.reservation.stayDates.until,
                home.reservation.listingCollection ===
                ListingCollectionEnum.Premier,
                home.reservation.listingCollection ===
                ListingCollectionEnum.Lifestyle
              )}
            </Typography>
          </Box>
          <DesktopHomeCard
            banner={statusBanner}
            isPastTrips={isPastTrips}
            home={home}
            isCancel={isCancel}
            confirmationCodeClassName={confirmationCodeClassName}
            isMobile={isMobile}
            expandedCard={expandedCard}
            onExpandCard={onExpandCard}
            setOpenModal={setOpenModal}
            moreActions={moreActions}
          />
        </div>
      )}
    </Box>
  );
};
